import React, { useState } from 'react';
import { FormContext, useForm } from 'react-hook-form';
import {
  buildSendoutInterviewPayload,
  SendoutInterviewForm
} from 'features/sendouts/components/forms/SendoutInterviewForm';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import DecisionDialog from 'UI/components/organisms/DecisionDialog';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';

import { getInterviewTimeValidationError } from '../CreateSendoutDrawer/CreateSendoutDrawer.utils';
import { FORM_FIELDS_MAP } from '../CreateSendoutDrawer/SendoutForm/SendoutForm.constants';

const CLOSE_FORM_STRINGS = strings.sendoutsV2.alerts.confirmationDialogs.closeForm;
const INTERVIEW_DOCUMENT_TYPE = CLOSE_FORM_STRINGS.documentTypes.interview;

export const CreateSendoutInterviewDrawer = ({
  isSubmitting = false,
  onClose = () => {},
  onSaved = () => {}
}) => {
  const form = useForm();
  const { clearError, handleSubmit, setError } = form;
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);

  const handleSave = async formData => {
    const interviewTimeValidationError = getInterviewTimeValidationError(formData);

    if (interviewTimeValidationError) {
      setError(
        interviewTimeValidationError.fieldKey,
        'validate',
        interviewTimeValidationError.message
      );
      return;
    }

    clearError(FORM_FIELDS_MAP.InterviewFromHour.key);
    clearError(FORM_FIELDS_MAP.InterviewToHour.key);

    const payload = buildSendoutInterviewPayload(formData);

    await onSaved({ payload });
  };

  const handleClose = () => {
    if (isSubmitting) return;

    setIsCloseDialogOpen(true);
  };

  const handleConfirmDrawerClose = () => {
    setIsCloseDialogOpen(false);
    onClose();
  };

  const CustomCloseDialogActions = ({ defaultButtonsProps, ActionsMapper }) => {
    const { onClose: closeDialog } = defaultButtonsProps;

    return (
      <ActionsMapper
        buttons={[
          {
            variant: 'outlined',
            text: CLOSE_FORM_STRINGS.actions.discardAndExit,
            onClick: handleConfirmDrawerClose
          },
          {
            text: strings.formatString(CLOSE_FORM_STRINGS.actions.cancel, {
              documentType: INTERVIEW_DOCUMENT_TYPE
            }),
            onClick: closeDialog
          }
        ]}
      />
    );
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        onClick: handleClose,
        disabled: isSubmitting
      },
      GridItemProps: { md: 3 }
    },
    {
      props: {
        text: strings.shared.ui.save,
        variant: 'contained',
        onClick: handleSubmit(handleSave),
        disabled: isSubmitting,
        isSaving: isSubmitting
      },
      GridItemProps: { md: 3 },
      mode: 'save'
    }
  ];

  return (
    <>
      <FPDrawer
        FPDrawerProps={{ open: true }}
        CardHeaderProps={{ title: strings.sendoutsV2.drawers.createSendoutInterview.title }}
        onClose={handleClose}
        footer={
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        }
      >
        <FormContext {...form}>
          <SendoutInterviewForm />
        </FormContext>
      </FPDrawer>

      <When condition={isCloseDialogOpen}>
        <DecisionDialog
          CustomActions={CustomCloseDialogActions}
          isOpened
          message={strings.formatString(CLOSE_FORM_STRINGS.message, {
            documentType: INTERVIEW_DOCUMENT_TYPE
          })}
          onClose={() => setIsCloseDialogOpen(false)}
          onConfirm={handleConfirmDrawerClose}
          showSeverityIcon
          title={strings.formatString(CLOSE_FORM_STRINGS.title, {
            documentType: INTERVIEW_DOCUMENT_TYPE
          })}
        />
      </When>
    </>
  );
};
